import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import moment from 'moment';
import { firstValueFrom, Subject } from 'rxjs';
import { LocationProfile } from 'src/app/types/locationProfile';
import { OperatingHours } from 'src/app/types/operatingHours';
import { ApiService } from '../../api.service';
import { LocalizableComponent } from '../../components/localizable/localizable.component';
import { TrailerRequestResponse } from '../../types/trailerRequestResponse';



@Component({
  selector: 'contact-request',
  templateUrl: './contact-modal.component.html',
  styleUrls: ['./contact-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContactModal
  extends LocalizableComponent
  implements OnInit, OnDestroy {
  contact: LocationProfile = null;
  disconnect$: Subject<boolean> = new Subject<boolean>();
  isLoading: boolean = true;

  @Input() request: TrailerRequestResponse;
  @Input() modal;

  hasHours: boolean = false;
  hours: string[] = [
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Sunday hours
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Monday hours
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Tuesday hours
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Wednesday hours
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Thursday hours
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Friday hours
    this.localize(this.langSection.ContactModal, this.langText.NoAvailableInformation), // Saturday hours
  ];

  constructor(private apiService: ApiService) {
    super();
  }

  async ngOnInit() {
    try {
      if (this.request.responsibleSiteCountryCode && this.request.responsibleSiteSlicNumber) {
        this.contact = await firstValueFrom(this.apiService.getLocation(this.request.responsibleSiteCountryCode, this.request.responsibleSiteSlicNumber));
      }

      if (this.contact === null) {
        this.contact = await firstValueFrom(this.apiService.getDefaultResponsibleSiteFromLocation(this.request.slicCountryCode, this.request.slicNumber));
        if (this.contact === null && this.request.trailerLoads?.length > 1 && this.request.trailerLoads[1].destinationCountryCode && this.request.trailerLoads[1].destinationOrganizationNumber) {
          this.contact = await firstValueFrom(this.apiService.getLocation(this.request.trailerLoads[1].destinationCountryCode, this.request.trailerLoads[1].destinationOrganizationNumber));
        }
      }

      this.hasHours = !!this.contact?.operatingHours?.length;
      if (this.hasHours) {
        this.initHours(this.contact?.operatingHours);
      }
    } catch (err) {
      console.log(err);
    }

    this.isLoading = false;
  }

  initHours(operatingHours: OperatingHours[]) {
    const isFrench = this.isFrench;
    const open24HoursText = this.localize(this.langSection.ContactModal, this.langText.Open24Hours);
    const closedText = this.localize(this.langSection.ContactModal, this.langText.Closed);

    // Initialize all days with segments array
    const daySegments: string[][] = Array.from({ length: 7 }, () => []);

    for (const operatingHour of operatingHours) {
      if (this.is24Hour(operatingHour)) {
        daySegments[operatingHour.day] = [open24HoursText];
        continue;
      }

      const openTime = moment(operatingHour.openTime, "HH:mm");
      const closeTime = moment(operatingHour.closeTime, "HH:mm");

      if (this.isOverlapToNextDay(operatingHour)) {
        // Split overnight hours into two segments
        const currentDaySegment = isFrench 
          ? `${operatingHour.openTime} - 23:59`
          : `${openTime.format('hh:mm A')} - ${moment("23:59", "HH:mm").format('hh:mm A')}`;
        
        const nextDaySegment = isFrench
          ? `00:00 - ${operatingHour.closeTime}`
          : `${moment("00:00", "HH:mm").format('hh:mm A')} - ${closeTime.format('hh:mm A')}`;

        daySegments[operatingHour.day].push(currentDaySegment);
        daySegments[(operatingHour.day + 1) % 7].push(nextDaySegment);
      } else {
        const segment = isFrench
          ? `${operatingHour.openTime} - ${operatingHour.closeTime}`
          : `${openTime.format('hh:mm A')} - ${closeTime.format('hh:mm A')}`;
        
        daySegments[operatingHour.day].push(segment);
      }
    }

    // Build final hours array
    this.hours = daySegments.map(segments => {
      if (segments.length === 0) return closedText;
      if (segments.includes(open24HoursText)) return open24HoursText;
      return segments.join(' & ');
    });
  }

  isOverlapToNextDay(operatingHour: OperatingHours): boolean {
    let openTime = moment(operatingHour.openTime, "HH:mm");
    let closeTime = moment(operatingHour.closeTime, "HH:mm");
    return (closeTime < openTime);
  }

  is24Hour(operatingHour: OperatingHours): boolean {
    const allDayFrom: string = "00:00";
    const allDayTo: string = "23:59";
    const midnight: string = "00:00";

    return ((operatingHour.openTime === allDayFrom && operatingHour.closeTime === allDayTo) || (operatingHour.openTime === midnight && operatingHour.closeTime === midnight));
  }

  ngOnDestroy() {
    this.disconnect$.next(true);
    this.disconnect$.unsubscribe();
  }

  dismissModal() {
    this.modal.close();
  }
}
